import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import Modal from 'modules/views/shared/Modal';
import { IRequiredCertificationGroup } from 'models/certifications';

import { useStylesCertificationWarningModal } from '../../Certifications/styles';

interface RequiredCertificateDetailsModalProps {
  isVisible: boolean;
  projectName: string;
  certificationGroups: IRequiredCertificationGroup[];
  onClose: () => void;
  onAddCertificate: () => void;
}

const getEffectiveDateText = (group: IRequiredCertificationGroup, t: (key: string, params?: any) => string): string => {
  const gracePeriodInDays = group.existingWorkerGracePeriodInDays || group.newWorkerGracePeriodInDays || 0;
  const mandatoryDeadline = group.existingWorkerMandatoryDeadline || group.newWorkerMandatoryDeadline;

  if (mandatoryDeadline) {
    if (gracePeriodInDays > 0) {
      return t('certifications.fcaGracePeriodPreactiveWorkerPeriodText', {
        gracePeriodInDays: gracePeriodInDays.toString(),
        date: moment(mandatoryDeadline).format('ll'),
      });
    } else {
      return t('certifications.fcaEffectiveFrom', {
        date: moment(mandatoryDeadline).format('ll'),
      });
    }
  }

  return '';
};

const shouldShowDateText = (group: IRequiredCertificationGroup): boolean => {
  return !!(group.existingWorkerMandatoryDeadline || group.newWorkerMandatoryDeadline);
};

const RequiredCertificateDetailsModal: React.FC<RequiredCertificateDetailsModalProps> = ({
  isVisible,
  projectName,
  certificationGroups,
  onClose,
  onAddCertificate,
}) => {
  const classes = useStylesCertificationWarningModal();
  const { t } = useTranslation();

  return (
    <Modal
      title={`${t('myCertifications.details.title')}`}
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      disableScrollLock={true}
      testId="required-certificate-details-modal"
      Content={
        <div className={classes.content}>
          {certificationGroups.map((group, groupIndex) => (
            <div
              key={group.id}
              className={classes.section}
              data-testid={`required-certificate-details-group-${group.id}`}
            >
              <p className={classes.sectionTitle}>
                {group.validationType === 3
                  ? t('myCertifications.details.pleaseAddAll')
                  : group.validationType === 2
                    ? t('myCertifications.details.pleaseAddAtLeastOne')
                    : t('myCertifications.details.optional')}{' '}
                "{group.name}"{t('myCertifications.details.certifications')}
                {shouldShowDateText(group) ? ` (${getEffectiveDateText(group, t)})` : ''}
              </p>
              <ul className={classes.certificationList}>
                {group.certifications.map((certification, index) => (
                  <li
                    key={certification.id}
                    className={classes.listItem}
                    data-testid={`required-certificate-details-item-${certification.id}`}
                  >
                    {certification.alias || certification.name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      }
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          styleClass: classes.cancelButtonViewMore,
          testId: 'required-certificate-details-cancel-button',
          onClick: onClose,
        },
        {
          label: t('myCertifications.card.addCertificate'),
          styleClass: classes.addCertificationButton,
          testId: 'required-certificate-details-add-button',
          onClick: () => {
            onClose();
            onAddCertificate();
          },
        },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(RequiredCertificateDetailsModal);
